// src/pages/Admin.js
import { useState, useEffect } from "react";
import {
  getDashboardStats, getAllShipments, getRiders, getStaff,
  createUser, getPickupRequests, convertPickupToShipment, assignRider
} from "../services/api";

const BASE = "https://api.pulselogistics.com.ng/api";

const statusStyle = s => ({
  delivered:        { color: "#16A34A", bg: "#DCFCE7" },
  in_transit:       { color: "#00A896", bg: "rgba(0,198,215,.1)" },
  out_for_delivery: { color: "#00A896", bg: "rgba(0,198,215,.1)" },
  pending:          { color: "#D97706", bg: "#FEF3C7" },
  picked_up:        { color: "#D97706", bg: "#FEF3C7" },
  cancelled:        { color: "#DC2626", bg: "#FEE2E2" },
}[s] || { color: "#6B7280", bg: "#EEF4F7" });

const StatusBadge = ({ status }) => {
  const s = statusStyle(status);
  return (
    <span style={{ padding: "3px 10px", borderRadius: 20, fontWeight: 600, fontSize: 11, background: s.bg, color: s.color, textTransform: "capitalize" }}>
      {status?.replace(/_/g, " ")}
    </span>
  );
};

export default function Admin() {
  const [tab, setTab]               = useState("overview");
  const [stats, setStats]           = useState(null);
  const [orders, setOrders]         = useState([]);
  const [riders, setRiders]         = useState([]);
  const [staff, setStaff]           = useState([]);   // ← FIXED: moved inside component
  const [requests, setRequests]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [converting, setConverting] = useState(null);
  const [convertMsg, setConvertMsg] = useState("");
  const [showModal, setShowModal]   = useState(false);
  const [newUser, setNewUser]       = useState({ name: "", email: "", password: "", role: "rider", phone: "" });
  const [userMsg, setUserMsg]       = useState("");
  const [assigningOrder, setAssigningOrder] = useState(null);
  const [assignMsg, setAssignMsg]   = useState("");

  // Password reset
  const [resetModal, setResetModal]   = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [resetMsg, setResetMsg]       = useState("");
  const [allUsers, setAllUsers]       = useState([]);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    const [s, o, r, p, st] = await Promise.all([
      getDashboardStats(), getAllShipments(), getRiders(), getPickupRequests(), getStaff(),
    ]);
    if (s?.success) setStats(s.stats);
    if (o?.success) setOrders(o.shipments || []);
    if (r?.success) {
      setRiders(r.riders || []);
      setAllUsers(r.riders || []);
    }
    if (p?.success) setRequests(p.requests || []);
    if (st?.success) setStaff(st.staff || []);   // ← NEW
    setLoading(false);
  };

  // Combined list for Riders & Staff table
  const allStaff = [...riders, ...staff];

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) return setUserMsg("Fill all required fields");
    const data = await createUser(newUser);
    if (data?.success) {
      setUserMsg("User created successfully!");
      setNewUser({ name: "", email: "", password: "", role: "rider", phone: "" });
      loadAll();
    } else {
      setUserMsg(data?.message || "Error creating user");
    }
  };

  const handleConvert = async (id) => {
    setConverting(id); setConvertMsg("");
    const data = await convertPickupToShipment(id);
    setConverting(null);
    if (data?.success) {
      setConvertMsg("AWB generated: " + data.awb + " — client will be notified on WhatsApp");
      setRequests(prev => prev.map(r => r.id === id ? { ...r, converted_to_shipment: true, awb: data.awb } : r));
      loadAll();
    } else {
      setConvertMsg(data?.message || "Error generating AWB");
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) return setResetMsg("Password must be at least 8 characters");
    setResetMsg("");
    try {
      const res = await fetch(`${BASE}/admin/users/${resetModal.id}/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ password: newPassword }),
      });
      const data = await res.json();
      if (data?.success) {
        setResetMsg("✅ Password reset successfully!");
        setTimeout(() => { setResetModal(null); setNewPassword(""); setResetMsg(""); }, 2000);
      } else {
        setResetMsg(data?.message || "Error resetting password");
      }
    } catch {
      setResetMsg("Connection error");
    }
  };

  const buildAWBWhatsApp = (req) => {
    const shipment = orders.find(o =>
      o.sender_name === req.sender_name &&
      o.sender_phone === req.sender_phone &&
      o.recipient_name === req.recipient_name
    );
    const awb = req.awb || shipment?.awb || "";
    const phone = (req.sender_phone || "").replace(/^0/, "234");
    const message = encodeURIComponent(
      `Hello ${req.sender_name}!\n\nYour Pulse Logistics pickup has been approved.\n\nTracking Number: ${awb}\n\nTrack your shipment at:\nhttps://pulselogistics.com.ng/track?awb=${awb}\n\nPulse Logistics - Delivering Tomorrow, Today.`
    );
    return `https://wa.me/${phone}?text=${message}`;
  };

  const kpis = stats ? [
    { label: "Total Orders",    value: stats.totalOrders,    delta: "All time",         accent: "#00C6D7", bg: "rgba(0,198,215,.1)", icon: "📦" },
    { label: "In Transit",      value: stats.inTransit,      delta: "Active now",       accent: "#D97706", bg: "#FEF3C7",            icon: "🚚" },
    { label: "Delivered Today", value: stats.deliveredToday, delta: "Great progress",   accent: "#16A34A", bg: "#DCFCE7",            icon: "✅" },
    { label: "Pending Pickup",  value: stats.pending,        delta: "Needs assignment", accent: "#DC2626", bg: "#FEE2E2",            icon: "⏳" },
  ] : [];

  if (loading) return <div style={{ background: "#F7FAFB", minHeight: "100vh" }}><div className="spinner" /></div>;

  return (
    <div style={{ background: "#F7FAFB", minHeight: "100vh" }}>

      {/* Header */}
      <div style={{ background: "linear-gradient(135deg,#0B1E3D,#1A3460)", padding: "24px 16px 40px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <div>
            <span className="tag tag-dark" style={{ marginBottom: 10, display: "inline-block" }}>ADMIN DASHBOARD</span>
            <h1 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 800, fontSize: 26, color: "#fff" }}>Operations Overview</h1>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn" onClick={loadAll} style={{ background: "rgba(255,255,255,.1)", color: "#fff", border: "1px solid rgba(255,255,255,.15)", fontSize: 13 }}>
              🔄 Refresh
            </button>
            <button className="btn btn-primary" onClick={() => setShowModal(true)} style={{ fontSize: 13 }}>
              + Add User
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 16px" }}>

        {/* KPI Cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 16, marginTop: -24, marginBottom: 28 }}>
          {kpis.map(k => (
            <div key={k.label} className="card" style={{ boxShadow: "0 4px 16px rgba(0,0,0,.08)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: "#6B7280", fontWeight: 500 }}>{k.label}</div>
                <div style={{ width: 28, height: 28, borderRadius: 7, background: k.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>{k.icon}</div>
              </div>
              <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 800, fontSize: 28, color: k.accent }}>{k.value ?? "0"}</div>
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: "#9CA3AF", marginTop: 6 }}>{k.delta}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="tab-bar">
          {[
            { id: "overview",  label: "Overview" },
            { id: "orders",    label: "Orders" },
            { id: "riders",    label: "Riders & Staff" },
            { id: "requests",  label: "Pickup Requests" },
          ].map(t => (
            <button key={t.id} className={`tab-btn${tab === t.id ? " active" : ""}`} onClick={() => setTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Orders */}
        {(tab === "overview" || tab === "orders") && (
          <div className="card" style={{ marginBottom: 24, overflowX: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 700, fontSize: 16, color: "#0B1E3D" }}>
                {tab === "overview" ? "Recent Orders" : "All Orders"}
              </div>
              {tab === "overview" && <button className="btn btn-ghost btn-sm" onClick={() => setTab("orders")}>View all</button>}
            </div>
            {orders.length === 0 ? (
              <p style={{ color: "#9CA3AF", fontSize: 13, textAlign: "center", padding: "24px 0" }}>No shipments yet.</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr><th>AWB</th><th>Sender</th><th>Recipient</th><th>Rider</th><th>Status</th><th>Date</th><th>Action</th></tr>
                </thead>
                <tbody>
                  {(tab === "overview" ? orders.slice(0, 5) : orders).map(o => (
                    <tr key={o.id || o.awb}>
                      <td style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: "#00A896", fontWeight: 600 }}>{o.awb}</td>
                      <td style={{ color: "#0B1E3D", fontWeight: 500 }}>{o.sender_name}</td>
                      <td style={{ color: "#374151" }}>{o.recipient_name}</td>
                      <td style={{ color: o.rider_name ? "#374151" : "#DC2626", fontWeight: o.rider_name ? 400 : 600 }}>{o.rider_name || "⚠ Unassigned"}</td>
                      <td><StatusBadge status={o.status} /></td>
                      <td style={{ color: "#9CA3AF", fontFamily: "'JetBrains Mono',monospace", fontSize: 11 }}>{new Date(o.created_at).toLocaleDateString("en-NG")}</td>
                      <td>
                        {!o.rider_name && (
                          <button className="btn btn-primary btn-sm" onClick={() => { setAssigningOrder(o); setAssignMsg(""); }}>
                            Assign Rider
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {assignMsg && <div className={`alert ${assignMsg.includes("✅") ? "alert-success" : "alert-error"}`} style={{ marginTop: 16 }}>{assignMsg}</div>}
          </div>
        )}

        {/* Riders & Staff — now shows ALL users (riders + admins + CS) */}
        {(tab === "overview" || tab === "riders") && (
          <div className="card" style={{ marginBottom: 32 }}>
            <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 700, fontSize: 16, color: "#0B1E3D", marginBottom: 20 }}>Riders & Staff</div>
            {allStaff.length === 0 ? (
              <p style={{ color: "#9CA3AF", fontSize: 13 }}>No users yet. Use + Add User to create one.</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Action</th></tr>
                </thead>
                <tbody>
                  {allStaff.map(r => (
                    <tr key={r.id}>
                      <td style={{ fontWeight: 600, color: "#0B1E3D" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg,#00C6D7,#00A896)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 13 }}>{r.name[0]}</div>
                          {r.name}
                        </div>
                      </td>
                      <td style={{ color: "#6B7280", fontSize: 13 }}>{r.email}</td>
                      <td><span className={`tag ${r.role === "admin" ? "tag-cyan" : r.role === "customer_service" ? "tag-navy" : "tag-amber"}`}>{r.role}</span></td>
                      <td><span style={{ padding: "3px 8px", borderRadius: 20, fontSize: 10, fontWeight: 600, background: r.is_active ? "#DCFCE7" : "#E2EBF0", color: r.is_active ? "#16A34A" : "#6B7280" }}>{r.is_active ? "ACTIVE" : "INACTIVE"}</span></td>
                      <td>
                        <button className="btn btn-ghost btn-sm" onClick={() => { setResetModal(r); setNewPassword(""); setResetMsg(""); }}>
                          🔐 Reset Password
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Pickup Requests */}
        {(tab === "overview" || tab === "requests") && (
          <div className="card" style={{ marginBottom: 32, overflowX: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 700, fontSize: 16, color: "#0B1E3D" }}>Pickup Requests</div>
              <button className="btn btn-ghost btn-sm" onClick={loadAll}>🔄 Refresh</button>
            </div>
            {convertMsg && <div className={`alert ${convertMsg.startsWith("AWB") ? "alert-success" : "alert-error"}`} style={{ marginBottom: 16 }}>{convertMsg}</div>}
            {requests.length === 0 ? (
              <p style={{ color: "#9CA3AF", fontSize: 13, textAlign: "center", padding: "24px 0" }}>No pickup requests yet.</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr><th>Sender</th><th>Phone</th><th>From</th><th>Recipient</th><th>To</th><th>Type</th><th>Date</th><th>Status</th><th>Action</th></tr>
                </thead>
                <tbody>
                  {requests.map((r, i) => (
                    <tr key={r.id || i}>
                      <td style={{ color: "#0B1E3D", fontWeight: 500 }}>{r.sender_name}</td>
                      <td style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: "#6B7280" }}>{r.sender_phone}</td>
                      <td style={{ color: "#374151", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.sender_address}</td>
                      <td style={{ color: "#0B1E3D", fontWeight: 500 }}>{r.recipient_name}</td>
                      <td style={{ color: "#374151", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.recipient_address}</td>
                      <td><span className="tag tag-cyan" style={{ textTransform: "capitalize" }}>{r.shipment_type}</span></td>
                      <td style={{ color: "#9CA3AF", fontFamily: "'JetBrains Mono',monospace", fontSize: 11 }}>{new Date(r.created_at).toLocaleDateString("en-NG")}</td>
                      <td>{r.converted_to_shipment ? <span className="tag tag-green">Approved</span> : <span className="tag tag-amber">Pending</span>}</td>
                      <td>
                        {!r.converted_to_shipment ? (
                          <button className="btn btn-primary btn-sm" disabled={converting === r.id} onClick={() => handleConvert(r.id)}>
                            {converting === r.id ? "Processing..." : "✅ Approve & Generate AWB"}
                          </button>
                        ) : (
                          <div style={{ display: "flex", gap: 6, flexDirection: "column" }}>
                            <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: "#00A896" }}>Converted ✓</span>
                            <a href={buildAWBWhatsApp(r)} target="_blank" rel="noopener noreferrer" className="btn btn-wa btn-sm" style={{ justifyContent: "center" }}>💬 Send AWB</a>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

      </div>

      {/* Add User Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); setUserMsg(""); }}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 800, fontSize: 18, color: "#0B1E3D", marginBottom: 20 }}>Add New User</div>
            <div className="form-group"><label>Full Name *</label><input value={newUser.name} onChange={e => setNewUser(u => ({ ...u, name: e.target.value }))} placeholder="e.g. Ade Musa" /></div>
            <div className="form-group"><label>Email *</label><input type="email" value={newUser.email} onChange={e => setNewUser(u => ({ ...u, email: e.target.value }))} placeholder="rider@pulse.ng" /></div>
            <div className="form-group"><label>Password *</label><input type="password" value={newUser.password} onChange={e => setNewUser(u => ({ ...u, password: e.target.value }))} placeholder="Min. 8 characters" /></div>
            <div className="form-group"><label>Phone</label><input value={newUser.phone} onChange={e => setNewUser(u => ({ ...u, phone: e.target.value }))} placeholder="08012345678" /></div>
            <div className="form-group">
              <label>Role</label>
              <select value={newUser.role} onChange={e => setNewUser(u => ({ ...u, role: e.target.value }))}>
                <option value="rider">🏍️ Rider</option>
                <option value="customer_service">🎧 Customer Service</option>
                <option value="admin">⚙️ Admin</option>
              </select>
            </div>
            {userMsg && <div className={`alert ${userMsg.includes("created") ? "alert-success" : "alert-error"}`}>{userMsg}</div>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <button className="btn btn-ghost" style={{ justifyContent: "center" }} onClick={() => { setShowModal(false); setUserMsg(""); }}>Cancel</button>
              <button className="btn btn-primary" style={{ justifyContent: "center" }} onClick={handleCreateUser}>Create User</button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetModal && (
        <div className="modal-overlay" onClick={() => { setResetModal(null); setNewPassword(""); setResetMsg(""); }}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 800, fontSize: 18, color: "#0B1E3D", marginBottom: 6 }}>Reset Password</div>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: "#6B7280", marginBottom: 20 }}>{resetModal.name} · {resetModal.email}</div>
            <div className="form-group">
              <label>New Password</label>
              <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min. 8 characters" />
            </div>
            {resetMsg && <div className={`alert ${resetMsg.startsWith("✅") ? "alert-success" : "alert-error"}`}>{resetMsg}</div>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <button className="btn btn-ghost" style={{ justifyContent: "center" }} onClick={() => { setResetModal(null); setNewPassword(""); setResetMsg(""); }}>Cancel</button>
              <button className="btn btn-primary" style={{ justifyContent: "center" }} onClick={handleResetPassword}>Reset Password</button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Rider Modal — uses riders only (not admins/CS) */}
      {assigningOrder && (
        <div className="modal-overlay" onClick={() => setAssigningOrder(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 800, fontSize: 18, color: "#0B1E3D", marginBottom: 4 }}>Assign Rider</div>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: "#6B7280", marginBottom: 20 }}>{assigningOrder.awb} · {assigningOrder.recipient_name}</div>
            {riders.filter(r => r.is_active).length === 0 ? (
              <p style={{ color: "#9CA3AF", fontSize: 13, marginBottom: 20 }}>No active riders. Add one with + Add User.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
                {riders.filter(r => r.is_active).map(r => (
                  <button key={r.id} className="btn btn-ghost" style={{ justifyContent: "flex-start", gap: 12 }}
                    onClick={async () => {
                      const data = await assignRider(assigningOrder.awb, r.id);
                      if (data?.success) { setAssignMsg("✅ Rider assigned successfully!"); setAssigningOrder(null); loadAll(); }
                      else setAssignMsg(data?.message || "Assignment failed");
                    }}>
                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg,#00C6D7,#00A896)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, color: "#fff", fontSize: 14, flexShrink: 0 }}>{r.name[0]}</div>
                    <div style={{ textAlign: "left" }}>
                      <div style={{ fontWeight: 600, color: "#0B1E3D", fontSize: 14 }}>{r.name}</div>
                      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: "#6B7280" }}>{r.phone || r.email}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            <button className="btn btn-ghost btn-full" onClick={() => setAssigningOrder(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}